import { useState, useRef, useCallback, useEffect } from "react";
import * as tus from "tus-js-client";
import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { differenceInDays, differenceInHours } from "date-fns";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Upload, Scissors, Play, Download, Sparkles, ChevronRight, ChevronDown, Check,
  Clock, Zap, TrendingUp, Star, Film, Loader2, RefreshCw, X, FileVideo,
  AlertCircle, Hash, Type, Flame, Laugh, Lightbulb, Heart, Trophy, Wand2,
  FolderOpen, Plus, Trash2, ArrowLeft, CalendarClock, ShoppingBag, Monitor,
  Smartphone, Square, PackageOpen, Info, ChevronUp,
} from "lucide-react";
import clipaiLogo from "@/assets/clipai-logo-new.png";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4;
type ViewMode = "projects" | "wizard";
type ClipLength = "0-5" | "0-8" | "8-15" | "custom";
type AspectRatio = "9:16" | "1:1" | "16:9";

interface SavedProject {
  id: string;
  title: string;
  video_name: string | null;
  status: string;
  clip_count: number;
  clips_data: any;
  render_data: any;
  render_status: string | null;
  caption_style: string;
  platform: string;
  created_at: string;
  expires_at: string;
}

interface ClipMoment {
  clip_index: number;
  title: string;
  hook: string;
  start_time_seconds: number;
  end_time_seconds: number;
  duration_seconds: number;
  engagement_score: number;
  reason: string;
  emotion: string;
  caption_text: string;
  platform_tags: string[];
}

interface RenderState {
  renderId: string;
  status: "planned" | "rendering" | "succeeded" | "failed";
  url: string | null;
  error?: string;
}

type CaptionStyle = {
  id: string;
  label: string;
  description: string;
  preview: string;
  className: string;
};

const CAPTION_STYLES: CaptionStyle[] = [
  { id: "minimal", label: "Minimal Clean", description: "Simple white text, subtle shadow", preview: "This is minimal", className: "text-white text-sm font-medium drop-shadow-sm" },
  { id: "bold", label: "Bold Viral", description: "Big bold yellow text, black stroke", preview: "BOLD VIRAL", className: "text-yellow-400 text-base font-black uppercase tracking-wide" },
  { id: "high-contrast", label: "High Contrast", description: "White text on black background", preview: "High Contrast", className: "bg-black text-white text-sm font-semibold px-2 py-0.5 rounded" },
  { id: "word-by-word", label: "Word-by-Word", description: "Animated one word at a time", preview: "Word • By • Word", className: "text-white text-lg font-bold tracking-widest" },
  { id: "brand", label: "Brand Color", description: "Uses your brand primary color", preview: "Brand Style", className: "text-primary text-sm font-bold" },
];

const CLIP_PRESETS: { id: ClipLength; label: string; range: string; icon: typeof Zap }[] = [
  { id: "0-5", label: "Quick Hits", range: "0–5 sec", icon: Zap },
  { id: "0-8", label: "Short Clips", range: "0–8 sec", icon: Scissors },
  { id: "8-15", label: "Standard", range: "8–15 sec", icon: Film },
  { id: "custom", label: "Custom", range: "Set range", icon: Wand2 },
];

const CLIP_COUNTS = [5, 10, 15, 20];

const PLATFORMS = [
  { id: "shorts", label: "YouTube Shorts" },
  { id: "tiktok", label: "TikTok" },
  { id: "reels", label: "Instagram Reels" },
];

const ASPECT_RATIOS: { id: AspectRatio; label: string; desc: string; icon: typeof Smartphone }[] = [
  { id: "9:16", label: "9:16", desc: "TikTok, Reels, Shorts", icon: Smartphone },
  { id: "1:1", label: "1:1", desc: "Instagram, LinkedIn", icon: Square },
  { id: "16:9", label: "16:9", desc: "YouTube", icon: Monitor },
];

const EMOTION_ICONS: Record<string, typeof Flame> = {
  excitement: Flame,
  insight: Lightbulb,
  humor: Laugh,
  surprise: Star,
  inspiration: Heart,
  authority: Trophy,
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  const steps = [
    { n: 1, label: "Upload" },
    { n: 2, label: "Configure" },
    { n: 3, label: "Generate" },
    { n: 4, label: "Download" },
  ];

  return (
    <div className="flex items-center gap-0 w-full max-w-xl mx-auto">
      {steps.map((step, i) => (
        <div key={step.n} className="flex items-center flex-1">
          <div className="flex flex-col items-center gap-1 min-w-0">
            <div
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
                current > step.n ? "bg-primary text-primary-foreground"
                  : current === step.n ? "bg-primary text-primary-foreground ring-4 ring-primary/30"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {current > step.n ? <Check className="w-4 h-4" /> : step.n}
            </div>
            <span className={cn("text-xs font-medium hidden sm:block", current >= step.n ? "text-foreground" : "text-muted-foreground")}>
              {step.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={cn("flex-1 h-0.5 mx-2 transition-all", current > step.n ? "bg-primary" : "bg-muted")} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Clip Card (Enhanced) ─────────────────────────────────────────────────────

function ClipCard({ clip, captionStyle, renderState, onRender, expiresAt }: {
  clip: ClipMoment;
  captionStyle: string;
  renderState?: RenderState;
  onRender: () => void;
  expiresAt?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const styleObj = CAPTION_STYLES.find((s) => s.id === captionStyle) || CAPTION_STYLES[0];
  const EmotionIcon = (clip.emotion && EMOTION_ICONS[clip.emotion]) || Sparkles;

  const scoreColor = clip.engagement_score >= 85 ? "bg-green-600" : clip.engagement_score >= 70 ? "bg-yellow-600" : "bg-muted";
  const isRendering = renderState?.status === "planned" || renderState?.status === "rendering";
  const isReady = renderState?.status === "succeeded" && renderState?.url;

  const expiryLabel = expiresAt ? (() => {
    const hours = differenceInHours(new Date(expiresAt), new Date());
    if (hours < 0) return "Expired";
    if (hours < 24) return `${hours}h left`;
    return `${differenceInDays(new Date(expiresAt), new Date())}d left`;
  })() : null;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/40 transition-all group">
      {/* Video preview */}
      <div className="relative aspect-[9/16] overflow-hidden bg-black">
        {isReady && renderState?.url ? (
          <video src={renderState.url} controls playsInline preload="metadata" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-muted/80 to-card flex flex-col items-end justify-end p-4">
            <div className="relative z-10 w-full">
              <p className={cn("w-full", styleObj.className)}>{clip.hook || clip.title}</p>
            </div>
            {isRendering && (
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 z-20">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <span className="text-sm text-white font-medium">Rendering…</span>
              </div>
            )}
          </div>
        )}
        {/* Score badge */}
        <div className={cn("absolute top-3 left-3 text-white text-xs font-bold px-2.5 py-1 rounded-full z-10", scoreColor)}>
          <Star className="w-3 h-3 inline mr-1" />{clip.engagement_score}/100
        </div>
        {/* Duration */}
        <div className="absolute top-3 right-3 bg-background/70 backdrop-blur-sm text-foreground text-xs font-medium px-2 py-1 rounded-md z-10">
          {formatTime(clip.duration_seconds)}
        </div>
        {/* Emotion tag */}
        {clip.emotion && (
          <div className="absolute bottom-3 left-3 bg-background/70 backdrop-blur-sm text-foreground text-xs font-medium px-2 py-1 rounded-md z-10 flex items-center gap-1 capitalize">
            <EmotionIcon className="w-3 h-3 text-primary" />{clip.emotion}
          </div>
        )}
        {/* Expiry */}
        {expiryLabel && (
          <div className={cn(
            "absolute bottom-3 right-3 text-xs font-medium px-2 py-1 rounded-md z-10",
            expiryLabel === "Expired" ? "bg-destructive/80 text-white" : "bg-background/70 backdrop-blur-sm text-muted-foreground"
          )}>
            <CalendarClock className="w-3 h-3 inline mr-1" />{expiryLabel}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 space-y-2">
        <h3 className="text-sm font-semibold text-foreground leading-tight line-clamp-2">{clip.title}</h3>

        {/* AI caption suggestion */}
        {clip.caption_text && (
          <p className="text-xs text-muted-foreground line-clamp-2 italic">"{clip.caption_text}"</p>
        )}

        {/* Expandable reason */}
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary flex items-center gap-1 hover:underline">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Why this clip?
        </button>
        {expanded && (
          <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-2">{clip.reason}</p>
        )}

        {/* Platform tags */}
        {clip.platform_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {clip.platform_tags.slice(0, 3).map(tag => (
              <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">#{tag}</Badge>
            ))}
          </div>
        )}

        {/* Actions */}
        {isReady ? (
          <Button size="sm" variant="default" className="w-full text-xs gap-1" onClick={() => window.open(renderState!.url!, '_blank')}>
            <Download className="w-3 h-3" />Download MP4
          </Button>
        ) : renderState?.status === "failed" ? (
          <Button size="sm" variant="outline" className="w-full text-xs gap-1" onClick={onRender}>
            <AlertCircle className="w-3 h-3" />Retry Render
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Platform Tips Section ────────────────────────────────────────────────────

function PlatformTips({ platform, clips }: { platform: string; clips: ClipMoment[] }) {
  const tips: Record<string, { bestTime: string; hashtagTip: string; captionLength: string }> = {
    shorts: { bestTime: "Fri–Sun, 3–6 PM", hashtagTip: "Use 3-5 hashtags max. Mix broad (#shorts) with niche-specific.", captionLength: "Keep under 100 chars. Front-load the hook." },
    tiktok: { bestTime: "Tue–Thu, 7–9 PM", hashtagTip: "Use 4-6 hashtags. Include one trending sound/hashtag.", captionLength: "150 chars max. Use emojis to break up text." },
    reels: { bestTime: "Mon–Wed, 11 AM – 1 PM", hashtagTip: "Use 5-10 hashtags. Mix sizes (500K–5M posts).", captionLength: "Under 125 chars for full visibility without 'more'." },
  };
  const t = tips[platform] || tips.shorts;
  const topTags = clips.flatMap(c => c.platform_tags || []).filter(Boolean);
  const uniqueTags = [...new Set(topTags)].slice(0, 8);

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-4">
      <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary" />
        Platform Tips for {PLATFORMS.find(p => p.id === platform)?.label || platform}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-foreground flex items-center gap-1"><Clock className="w-3 h-3 text-primary" /> Best Posting Time</p>
          <p className="text-xs text-muted-foreground">{t.bestTime}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-foreground flex items-center gap-1"><Hash className="w-3 h-3 text-primary" /> Hashtag Strategy</p>
          <p className="text-xs text-muted-foreground">{t.hashtagTip}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-foreground flex items-center gap-1"><Type className="w-3 h-3 text-primary" /> Caption Length</p>
          <p className="text-xs text-muted-foreground">{t.captionLength}</p>
        </div>
      </div>
      {uniqueTags.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-foreground">Suggested Hashtags</p>
          <div className="flex flex-wrap gap-1">
            {uniqueTags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs gap-1 cursor-pointer" onClick={() => { navigator.clipboard.writeText(`#${tag}`); toast.success("Hashtag copied ✓"); }}>
                #{tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ClipAI() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { consume } = useCredits();

  const [viewMode, setViewMode] = useState<ViewMode>("projects");
  const [savedProjects, setSavedProjects] = useState<SavedProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [step, setStep] = useState<Step>(1);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [currentExpiresAt, setCurrentExpiresAt] = useState<string | null>(null);

  // Config state
  const [clipLength, setClipLength] = useState<ClipLength>("8-15");
  const [customMin, setCustomMin] = useState(5);
  const [customMax, setCustomMax] = useState(30);
  const [clipCount, setClipCount] = useState(10);
  const [captionStyle, setCaptionStyle] = useState("minimal");
  const [platform, setPlatform] = useState("shorts");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("9:16");
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState("");

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genStatus, setGenStatus] = useState("");

  // Results
  const [clips, setClips] = useState<ClipMoment[]>([]);
  const [analysisSummary, setAnalysisSummary] = useState("");

  // Render state per clip
  const [renderStates, setRenderStates] = useState<Record<number, RenderState>>({});
  const [renderingAll, setRenderingAll] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  // Clip to Sell CTA state
  const [clipToSellEnabled, setClipToSellEnabled] = useState(false);
  const [ctaText, setCtaText] = useState("Link in bio to get my eBook");

  // ── Fetch projects ──
  const fetchProjects = useCallback(async () => {
    if (!user) return;
    setLoadingProjects(true);
    try {
      const { data, error } = await supabase
        .from("clip_jobs")
        .select("id, title, video_name, status, clip_count, clips_data, render_data, render_status, caption_style, platform, created_at, expires_at")
        .eq("user_id", user.id)
        .eq("status", "complete")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setSavedProjects((data as any as SavedProject[]) || []);
    } catch { /* silently fail */ } finally { setLoadingProjects(false); }
  }, [user]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const openSavedProject = (project: SavedProject) => {
    setJobId(project.id);
    setCaptionStyle(project.caption_style);
    setPlatform(project.platform);
    setClipCount(project.clip_count);
    setCurrentExpiresAt(project.expires_at);

    const clipsData = project.clips_data as any;
    if (clipsData?.clips) { setClips(clipsData.clips); setAnalysisSummary(clipsData.analysis_summary || ""); }

    const renderData = project.render_data as any;
    if (renderData?.renders) {
      const states: Record<number, RenderState> = {};
      for (const r of renderData.renders) {
        states[r.clipIndex] = { renderId: r.renderId || "", status: r.url ? "succeeded" : r.error ? "failed" : "rendering", url: r.url || null, error: r.error };
      }
      setRenderStates(states);
    }
    setStep(4);
    setViewMode("wizard");
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await supabase.from("clip_jobs").delete().eq("id", projectId);
      setSavedProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success("Project deleted");
    } catch { toast.error("Failed to delete project"); }
  };

  const getDaysRemaining = (expiresAt: string) => {
    const hours = differenceInHours(new Date(expiresAt), new Date());
    if (hours < 0) return "Expired";
    if (hours < 24) return `${hours}h left`;
    return `${differenceInDays(new Date(expiresAt), new Date())}d left`;
  };

  const startNewProject = () => {
    setViewMode("wizard"); setStep(1); setUploadedFile(null); setVideoUrl(null);
    setJobId(null); setClips([]); setAnalysisSummary(""); setGenProgress(0);
    setRenderStates({}); setTranscript(""); setCurrentExpiresAt(null);
  };

  // ── Polling ──
  const activePollingRef = useRef(false);
  const pendingRendersRef = useRef<Map<number, string>>(new Map());

  const startPollingLoop = useCallback(() => {
    if (activePollingRef.current) return;
    activePollingRef.current = true;
    const loop = async () => {
      const maxMs = 10 * 60 * 1000;
      const started = Date.now();
      while (pendingRendersRef.current.size > 0 && Date.now() - started < maxMs) {
        const entries = Array.from(pendingRendersRef.current.entries());
        for (const [clipIndex, renderId] of entries) {
          try {
            const { data, error } = await supabase.functions.invoke("render-clips", { body: { action: "check-status", renderId } });
            if (error) continue;
            if (data.status === "rate_limited") continue;
            if (data.status === "succeeded") { setRenderStates(prev => ({ ...prev, [clipIndex]: { renderId, status: "succeeded", url: data.url } })); pendingRendersRef.current.delete(clipIndex); }
            else if (data.status === "failed") { setRenderStates(prev => ({ ...prev, [clipIndex]: { renderId, status: "failed", url: null, error: data.error_message } })); pendingRendersRef.current.delete(clipIndex); }
            else { setRenderStates(prev => ({ ...prev, [clipIndex]: { ...prev[clipIndex], status: data.status as any } })); }
          } catch { /* retry */ }
          await new Promise(r => setTimeout(r, 300));
        }
        await new Promise(r => setTimeout(r, 6000));
      }
      activePollingRef.current = false;
    };
    loop();
  }, []);

  const enqueuePolling = useCallback((renderId: string, clipIndex: number) => {
    pendingRendersRef.current.set(clipIndex, renderId);
    startPollingLoop();
  }, [startPollingLoop]);

  // ── Render single/all ──
  const handleRenderSingle = useCallback(async (clip: ClipMoment) => {
    if (!jobId || !videoUrl) return;
    setRenderStates(prev => ({ ...prev, [clip.clip_index]: { renderId: "", status: "planned", url: null } }));
    try {
      const { data, error } = await supabase.functions.invoke("render-clips", {
        body: { action: "render-single", jobId, clip: { clipIndex: clip.clip_index, videoUrl, startTime: clip.start_time_seconds, endTime: clip.end_time_seconds, captionStyle, captionText: clip.caption_text, hookText: clip.hook, platform } },
      });
      if (error || data?.error) throw new Error(data?.error || "Render failed");
      setRenderStates(prev => ({ ...prev, [clip.clip_index]: { renderId: data.renderId, status: data.status === "succeeded" ? "succeeded" : "rendering", url: data.url } }));
      if (data.status !== "succeeded") enqueuePolling(data.renderId, clip.clip_index);
    } catch (err: any) {
      toast.error(err.message || "Render failed");
      setRenderStates(prev => ({ ...prev, [clip.clip_index]: { renderId: "", status: "failed", url: null, error: err.message } }));
    }
  }, [jobId, videoUrl, captionStyle, platform, enqueuePolling]);

  const handleRenderAll = useCallback(async () => {
    if (!jobId || !videoUrl || clips.length === 0) return;
    setRenderingAll(true);
    const initial: Record<number, RenderState> = {};
    clips.forEach(c => { initial[c.clip_index] = { renderId: "", status: "planned", url: null }; });
    setRenderStates(initial);
    try {
      const { data, error } = await supabase.functions.invoke("render-clips", {
        body: { action: "render-all", jobId, videoUrl, captionStyle, platform, clips: clips.map(c => ({ clipIndex: c.clip_index, videoUrl, startTime: c.start_time_seconds, endTime: c.end_time_seconds, captionStyle, captionText: c.caption_text, hookText: c.hook, platform })) },
      });
      if (error || data?.error) throw new Error(data?.error || "Render failed");
      for (const render of data.renders || []) {
        if (render.error) { setRenderStates(prev => ({ ...prev, [render.clipIndex]: { renderId: "", status: "failed", url: null, error: render.error } })); }
        else { setRenderStates(prev => ({ ...prev, [render.clipIndex]: { renderId: render.renderId, status: render.status === "succeeded" ? "succeeded" : "rendering", url: render.url } })); if (render.status !== "succeeded") enqueuePolling(render.renderId, render.clipIndex); }
      }
      toast.success(`Rendering ${clips.length} clips — this may take a few minutes`);
    } catch (err: any) { toast.error(err.message || "Render all failed"); }
    finally { setRenderingAll(false); }
  }, [jobId, videoUrl, clips, captionStyle, platform, enqueuePolling]);

  // ── Batch Download ZIP ──
  const handleDownloadAll = useCallback(async () => {
    const readyClips = clips.filter(c => renderStates[c.clip_index]?.status === "succeeded" && renderStates[c.clip_index]?.url);
    if (readyClips.length === 0) { toast.error("No rendered clips to download"); return; }

    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      for (const clip of readyClips) {
        const url = renderStates[clip.clip_index].url!;
        const response = await fetch(url);
        const blob = await response.blob();
        const fileName = `clip-${clip.clip_index + 1}-score-${clip.engagement_score}.mp4`;
        zip.file(fileName, blob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(zipBlob);
      a.download = "clips.zip";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${readyClips.length} clips as ZIP`);
    } catch { toast.error("Failed to create ZIP"); }
    finally { setDownloadingZip(false); }
  }, [clips, renderStates]);

  const getClipRange = () => {
    if (clipLength === "0-5") return { min: 0, max: 5 };
    if (clipLength === "0-8") return { min: 0, max: 8 };
    if (clipLength === "8-15") return { min: 8, max: 15 };
    return { min: customMin, max: customMax };
  };

  // ── Drag & Drop ──
  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback(() => { setIsDragging(false); }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("video/")) handleFileSelect(file);
    else toast.error("Please upload a video file");
  }, []);

  const handleFileSelect = (file: File) => {
    const MAX_SIZE = 2 * 1024 * 1024 * 1024;
    if (file.size > MAX_SIZE) { toast.error("File is too large. Maximum supported upload size is 2GB."); return; }
    setUploadedFile(file);
  };

  // ── Upload ──
  const handleUpload = async () => {
    if (!uploadedFile || !user) return;
    setUploading(true); setUploadProgress(5);
    try {
      const { data: job, error: jobError } = await supabase.from("clip_jobs").insert({ user_id: user.id, title: uploadedFile.name.replace(/\.[^/.]+$/, ""), video_name: uploadedFile.name, video_size_bytes: uploadedFile.size, status: "uploading" }).select().single();
      if (jobError || !job) throw new Error("Failed to create job");
      setJobId(job.id);
      setCurrentExpiresAt((job as any).expires_at);
      setUploadProgress(10);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const filePath = `${user.id}/${job.id}/${uploadedFile.name}`;
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;

      await new Promise<void>((resolve, reject) => {
        const upload = new tus.Upload(uploadedFile, {
          endpoint: `https://${projectId}.supabase.co/storage/v1/upload/resumable`,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          headers: { authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
          uploadDataDuringCreation: true, removeFingerprintOnSuccess: true,
          metadata: { bucketName: "clip-uploads", objectName: filePath, contentType: uploadedFile.type, cacheControl: "3600" },
          chunkSize: 6 * 1024 * 1024,
          onError: (error) => reject(error),
          onProgress: (bytesUploaded, bytesTotal) => setUploadProgress(Math.round((bytesUploaded / bytesTotal) * 80) + 10),
          onSuccess: () => resolve(),
        });
        upload.findPreviousUploads().then((prev) => { if (prev.length > 0) upload.resumeFromPreviousUpload(prev[0]); upload.start(); });
      });

      setUploadProgress(90);
      const { data: urlData } = supabase.storage.from("clip-uploads").getPublicUrl(filePath);
      const url = urlData?.publicUrl || "";
      setVideoUrl(url);
      await supabase.from("clip_jobs").update({ video_url: url, status: "ready" }).eq("id", job.id);
      setUploadProgress(100);
      toast.success("Video uploaded! Starting transcription...");
      setStep(2);
      autoTranscribe(job.id, user.id, uploadedFile);
    } catch (error: any) {
      const message = String(error?.message || "");
      if (message.includes("Maximum size exceeded") || message.includes("response code: 413")) toast.error("Upload rejected: max 2GB.");
      else toast.error(error.message || "Upload failed. You can retry — it will resume.");
    } finally { setUploading(false); }
  };

  // ── Audio extraction ──
  const writeWavString = (view: DataView, offset: number, str: string) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };

  const encodeWAV = (audioBuffer: AudioBuffer): Blob => {
    const sampleRate = audioBuffer.sampleRate;
    const samples = audioBuffer.getChannelData(0);
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    writeWavString(view, 0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true);
    writeWavString(view, 8, 'WAVE'); writeWavString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeWavString(view, 36, 'data'); view.setUint32(40, samples.length * 2, true);
    let offset = 44;
    for (let i = 0; i < samples.length; i++) { const s = Math.max(-1, Math.min(1, samples[i])); view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true); offset += 2; }
    return new Blob([buffer], { type: 'audio/wav' });
  };

  const extractAudioFromVideo = async (videoFile: File): Promise<Blob> => {
    const arrayBuffer = await videoFile.arrayBuffer();
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const decoded = await audioCtx.decodeAudioData(arrayBuffer);
    await audioCtx.close();
    const TARGET_SR = 16000;
    const offlineCtx = new OfflineAudioContext(1, Math.ceil(decoded.duration * TARGET_SR), TARGET_SR);
    const src = offlineCtx.createBufferSource();
    src.buffer = decoded; src.connect(offlineCtx.destination); src.start();
    const rendered = await offlineCtx.startRendering();
    return encodeWAV(rendered);
  };

  // ── Transcribe ──
  const autoTranscribe = async (transcribeJobId: string, userId: string, videoFile: File) => {
    setTranscribing(true); setTranscribeError(null);
    try {
      toast.info("Extracting audio from video…");
      let audioBlob: Blob;
      try { audioBlob = await extractAudioFromVideo(videoFile); }
      catch { setTranscribeError("Could not extract audio. Paste transcript manually below."); return; }

      const audioPath = `${userId}/${transcribeJobId}/audio-for-transcribe.mp4`;
      const audioFile = new File([audioBlob], "audio-for-transcribe.mp4", { type: "video/mp4" });
      const { error: uploadErr } = await supabase.storage.from("clip-uploads").upload(audioPath, audioFile, { contentType: "video/mp4", upsert: true });
      if (uploadErr) throw uploadErr;

      const { data, error } = await supabase.functions.invoke("transcribe-video", { body: { jobId: transcribeJobId, filePath: audioPath, fileSizeBytes: audioBlob.size } });
      if (error) throw error;
      if (data?.error) {
        if (data.code === "FILE_TOO_LARGE") { setTranscribeError("Audio too large. Paste transcript manually."); toast.info("Audio too large. Paste transcript manually."); }
        else throw new Error(data.error);
        return;
      }
      if (data?.transcript) { setTranscript(data.transcript); toast.success(`Transcription complete! ${data.wordCount || 0} words detected.`); }
    } catch (err: any) {
      const message = String(err?.message || "");
      if (message.toLowerCase().includes("mime type")) setTranscribeError("Auto-transcription blocked by format rules. Paste manually.");
      else setTranscribeError("Transcription failed — paste your transcript manually.");
      toast.error("Transcription failed.", { duration: 8000 });
    } finally { setTranscribing(false); }
  };

  // ── Generate ──
  const handleGenerate = async () => {
    if (!jobId || !user) return;
    setGenerating(true); setGenProgress(0); setStep(3);
    const statusMessages = ["Analyzing video content...", "Identifying high-retention moments...", "Scoring engagement potential...", "Detecting emotional peaks...", "Optimizing clip boundaries...", "Ranking clips by virality...", "Finalizing your clips..."];
    let msgIdx = 0;
    setGenStatus(statusMessages[0]);
    const progressInterval = setInterval(() => {
      setGenProgress(p => { const next = Math.min(p + Math.random() * 8, 90); if (next > msgIdx * 13 && msgIdx < statusMessages.length - 1) { msgIdx++; setGenStatus(statusMessages[msgIdx]); } return next; });
    }, 800);
    try {
      const credited = await consume("clip-analysis", "Clip AI analysis");
      if (!credited) { clearInterval(progressInterval); setGenerating(false); setStep(2); return; }
      const { min, max } = getClipRange();
      await supabase.from("clip_jobs").update({ clip_length_min: min, clip_length_max: max, clip_count: clipCount, caption_style: captionStyle, platform, status: "analyzing" }).eq("id", jobId);
      const { data, error } = await supabase.functions.invoke("analyze-clips", {
        body: { jobId, videoName: uploadedFile?.name, videoDurationSeconds: null, clipLengthMin: min, clipLengthMax: max, clipCount, captionStyle, platform, transcript: transcript || null },
      });
      clearInterval(progressInterval);
      if (error) throw error;
      if (data?.error) {
        if (data.error.includes("Rate limit")) toast.error("Rate limit — wait a moment.");
        else if (data.error.includes("credits")) toast.error("Credits exhausted.");
        else throw new Error(data.error);
        setGenerating(false); setStep(2); return;
      }
      setGenProgress(100); setGenStatus("Done! Now rendering your clips...");
      const generatedClips = data.clips || [];
      setClips(generatedClips); setAnalysisSummary(data.analysis_summary || "");
      setGenerating(false);
      if (generatedClips.length > 0 && jobId && videoUrl) autoRenderAllClips(generatedClips);
    } catch (error: any) { clearInterval(progressInterval); toast.error(error.message || "Generation failed"); setGenerating(false); setStep(2); }
  };

  const autoRenderAllClips = useCallback(async (generatedClips: ClipMoment[]) => {
    if (!jobId || !videoUrl || generatedClips.length === 0) return;
    setRenderingAll(true);
    const initial: Record<number, RenderState> = {};
    generatedClips.forEach(c => { initial[c.clip_index] = { renderId: "", status: "planned", url: null }; });
    setRenderStates(initial);
    try {
      const { data, error } = await supabase.functions.invoke("render-clips", {
        body: { action: "render-all", jobId, videoUrl, captionStyle, platform, clips: generatedClips.map(c => ({ clipIndex: c.clip_index, videoUrl, startTime: c.start_time_seconds, endTime: c.end_time_seconds, captionStyle, captionText: c.caption_text, hookText: c.hook, platform })) },
      });
      if (error || data?.error) throw new Error(data?.error || "Render failed");
      for (const render of data.renders || []) {
        if (render.error) setRenderStates(prev => ({ ...prev, [render.clipIndex]: { renderId: "", status: "failed", url: null, error: render.error } }));
        else { setRenderStates(prev => ({ ...prev, [render.clipIndex]: { renderId: render.renderId, status: render.status === "succeeded" ? "succeeded" : "rendering", url: render.url } })); if (render.status !== "succeeded") enqueuePolling(render.renderId, render.clipIndex); }
      }
      toast.success(`Rendering ${generatedClips.length} clips — previews will appear as they complete`);
    } catch (err: any) { toast.error(err.message || "Auto-render failed"); }
    finally { setRenderingAll(false); }
  }, [jobId, videoUrl, captionStyle, platform, enqueuePolling]);

  const renderedCount = Object.values(renderStates).filter(r => r.status === "succeeded").length;
  const renderingCount = Object.values(renderStates).filter(r => r.status === "rendering" || r.status === "planned").length;

  useEffect(() => { if (step === 3 && clips.length > 0 && renderingCount === 0 && renderedCount > 0) setStep(4); }, [step, clips.length, renderingCount, renderedCount]);

  // ──────────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src={clipaiLogo} alt="Clip AI" className="w-9 h-9 rounded-xl object-contain" />
          <div>
            <h1 className="text-base font-bold text-foreground">Clip AI</h1>
            <p className="text-xs text-muted-foreground">Turn one long video into 20 viral clips</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {viewMode === "wizard" && (
              <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => { setViewMode("projects"); fetchProjects(); }}>
                <ArrowLeft className="w-3 h-3" />My Projects
              </Button>
            )}
            <Badge variant="secondary" className="gap-1 text-xs"><Sparkles className="w-3 h-3" />AI-Powered</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* ── PROJECT LIST VIEW ───────────────────────────────────────────────────── */}
        {viewMode === "projects" && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Your Clip Projects</h2>
                <p className="text-muted-foreground text-sm">Clips are saved for 30 days. Download before they expire.</p>
              </div>
              <Button onClick={startNewProject} className="gap-2"><Plus className="w-4 h-4" />New Project</Button>
            </div>

            {loadingProjects ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : savedProjects.length === 0 ? (
              /* Enhanced empty state */
              <div className="text-center py-12 space-y-6">
                <div className="w-20 h-20 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Film className="w-10 h-10 text-primary" />
                </div>
                <div className="space-y-2">
                  <p className="text-xl font-bold text-foreground">Turn long videos into viral clips</p>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Upload a podcast, YouTube video, or webinar and our AI will find the most viral moments automatically.
                  </p>
                </div>

                {/* How it works */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto">
                  {[
                    { step: "1", icon: Upload, title: "Upload", desc: "Drop any video up to 2GB" },
                    { step: "2", icon: Sparkles, title: "AI Finds Clips", desc: "Detects viral moments" },
                    { step: "3", icon: Download, title: "Download", desc: "Get rendered clips" },
                  ].map(item => (
                    <div key={item.step} className="bg-card border border-border rounded-xl p-4 text-center space-y-2">
                      <div className="w-8 h-8 mx-auto bg-primary/10 rounded-full flex items-center justify-center text-primary text-sm font-bold">{item.step}</div>
                      <p className="text-sm font-semibold text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Supported formats */}
                <div className="bg-muted/50 rounded-xl p-4 max-w-md mx-auto space-y-2">
                  <p className="text-xs font-semibold text-foreground flex items-center justify-center gap-1"><Info className="w-3 h-3" /> Supported Formats</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {["MP4", "MOV", "AVI", "WebM", "MKV"].map(f => <Badge key={f} variant="outline" className="text-xs">{f}</Badge>)}
                  </div>
                  <p className="text-xs text-muted-foreground">Up to 2GB · Best results with 3–30 minute videos</p>
                </div>

                {/* Retention notice */}
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 max-w-md mx-auto flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-primary shrink-0" />
                  <p className="text-xs text-muted-foreground">Clips are stored for <span className="font-semibold text-foreground">30 days</span>. Download before they expire.</p>
                </div>

                <Button onClick={startNewProject} size="lg" className="gap-2"><Plus className="w-4 h-4" />Create First Project</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedProjects.map((project) => {
                  const clipsCount = (project.clips_data as any)?.clips?.length || 0;
                  const isComplete = project.status === "complete";
                  const daysLeft = getDaysRemaining(project.expires_at);
                  const isExpired = daysLeft === "Expired";
                  return (
                    <div key={project.id} className={cn("bg-card border border-border rounded-xl p-4 space-y-3 transition-all", isExpired ? "opacity-50" : "hover:border-primary/40 cursor-pointer")} onClick={() => !isExpired && isComplete && openSavedProject(project)}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-foreground truncate">{project.title}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{project.video_name || "video"}</p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive" onClick={(e) => e.stopPropagation()}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                            <AlertDialogHeader><AlertDialogTitle>Delete project?</AlertDialogTitle><AlertDialogDescription>This will permanently delete "{project.title}" and all clips.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteProject(project.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs gap-1"><Scissors className="w-3 h-3" />{clipsCount} clips</Badge>
                        <Badge variant={isExpired ? "destructive" : "secondary"} className="text-xs gap-1"><CalendarClock className="w-3 h-3" />{daysLeft}</Badge>
                        {isComplete ? <Badge variant="secondary" className="text-xs gap-1 text-green-500"><Check className="w-3 h-3" />Ready</Badge> : <Badge variant="secondary" className="text-xs">{project.status}</Badge>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── WIZARD VIEW ─────────────────────────────────────────────────────────── */}
        {viewMode === "wizard" && (
          <>
            <StepIndicator current={step} />

            {/* STEP 1: Upload */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-foreground">Upload Your Video</h2>
                  <p className="text-muted-foreground text-sm max-w-md mx-auto">Upload a podcast, YouTube video, webinar, or any long-form content. Up to 2GB.</p>
                </div>

                {/* Retention warning */}
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 max-w-lg mx-auto flex items-center gap-3">
                  <CalendarClock className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Clips stored for 30 days</p>
                    <p className="text-xs text-muted-foreground">Download your clips before they expire. We recommend downloading immediately after rendering.</p>
                  </div>
                </div>

                {/* Drop zone */}
                <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => !uploadedFile && fileInputRef.current?.click()}
                  className={cn("relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer",
                    isDragging ? "border-primary bg-primary/5 scale-[1.01]" : uploadedFile ? "border-primary/50 bg-primary/5 cursor-default" : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}>
                  <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileSelect(file); }} />
                  {uploadedFile ? (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto bg-primary/20 rounded-full flex items-center justify-center"><FileVideo className="w-8 h-8 text-primary" /></div>
                      <div><p className="font-semibold text-foreground">{uploadedFile.name}</p><p className="text-sm text-muted-foreground">{formatFileSize(uploadedFile.size)}</p></div>
                      <button onClick={(e) => { e.stopPropagation(); setUploadedFile(null); setVideoUrl(null); setJobId(null); }} className="text-xs text-destructive hover:underline">Remove file</button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center"><Upload className="w-7 h-7 text-muted-foreground" /></div>
                      <div><p className="font-semibold text-foreground">Drop your video here</p><p className="text-sm text-muted-foreground mt-1">or click to browse</p></div>
                      <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
                        {["MP4", "MOV", "AVI", "WebM", "MKV"].map(f => <Badge key={f} variant="outline" className="text-xs">{f}</Badge>)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Max file size: 2GB · Best results: 3–30 min videos</p>
                    </div>
                  )}
                </div>

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Uploading…</span><span className="text-foreground font-medium">{Math.round(uploadProgress)}%</span></div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                <div className="flex justify-center">
                  <Button size="lg" onClick={handleUpload} disabled={!uploadedFile || uploading} className="gap-2 px-8">
                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading…</> : <>Continue<ChevronRight className="w-4 h-4" /></>}
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                  {[
                    { icon: Zap, title: "AI-Powered Analysis", desc: "Detects high-engagement moments automatically" },
                    { icon: TrendingUp, title: "Platform Optimized", desc: "Formatted for Shorts, TikTok & Reels" },
                    { icon: Scissors, title: "Smart Cuts", desc: "Never cuts mid-sentence" },
                  ].map(item => (
                    <div key={item.title} className="bg-card border border-border rounded-xl p-4 flex gap-3">
                      <item.icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div><p className="text-sm font-semibold text-foreground">{item.title}</p><p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2: Configure */}
            {step === 2 && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-foreground">Configure Your Clips</h2>
                  <p className="text-muted-foreground text-sm">Customize how your clips are generated. File: <span className="font-medium text-foreground">{uploadedFile?.name}</span></p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left column */}
                  <div className="space-y-6">
                    {/* Clip length */}
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-foreground">Clip Length</label>
                      <div className="grid grid-cols-2 gap-2">
                        {CLIP_PRESETS.map(preset => (
                          <button key={preset.id} onClick={() => setClipLength(preset.id)}
                            className={cn("flex items-center gap-2 p-3 rounded-xl border text-left transition-all",
                              clipLength === preset.id ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40")}>
                            <preset.icon className="w-4 h-4 shrink-0" />
                            <div><p className="text-sm font-semibold">{preset.label}</p><p className="text-xs text-muted-foreground">{preset.range}</p></div>
                          </button>
                        ))}
                      </div>
                      {clipLength === "custom" && (
                        <div className="flex gap-3 items-center">
                          <div className="flex-1"><label className="text-xs text-muted-foreground">Min (sec)</label><Input type="number" min={0} max={customMax - 1} value={customMin} onChange={e => setCustomMin(Number(e.target.value))} className="mt-1" /></div>
                          <span className="text-muted-foreground mt-4">–</span>
                          <div className="flex-1"><label className="text-xs text-muted-foreground">Max (sec)</label><Input type="number" min={customMin + 1} max={300} value={customMax} onChange={e => setCustomMax(Number(e.target.value))} className="mt-1" /></div>
                        </div>
                      )}
                    </div>

                    {/* Clip count */}
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-foreground">Number of Clips</label>
                      <div className="flex gap-2">
                        {CLIP_COUNTS.map(n => (
                          <button key={n} onClick={() => setClipCount(n)}
                            className={cn("flex-1 py-2.5 rounded-xl border text-sm font-bold transition-all",
                              clipCount === n ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40")}>{n}</button>
                        ))}
                      </div>
                    </div>

                    {/* Platform */}
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-foreground">Target Platform</label>
                      <div className="flex gap-2">
                        {PLATFORMS.map(p => (
                          <button key={p.id} onClick={() => setPlatform(p.id)}
                            className={cn("flex-1 py-2 rounded-xl border text-xs font-semibold transition-all",
                              platform === p.id ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40 text-muted-foreground")}>{p.label}</button>
                        ))}
                      </div>
                    </div>

                    {/* Aspect Ratio */}
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-foreground">Aspect Ratio</label>
                      <div className="flex gap-2">
                        {ASPECT_RATIOS.map(ar => (
                          <button key={ar.id} onClick={() => setAspectRatio(ar.id)}
                            className={cn("flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border text-xs font-semibold transition-all",
                              aspectRatio === ar.id ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40 text-muted-foreground")}>
                            <ar.icon className="w-4 h-4" />
                            <span>{ar.label}</span>
                            <span className="text-[10px] text-muted-foreground font-normal">{ar.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right column */}
                  <div className="space-y-6">
                    {/* Caption style */}
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-foreground">Caption Style</label>
                      <div className="space-y-2">
                        {CAPTION_STYLES.map(style => (
                          <button key={style.id} onClick={() => setCaptionStyle(style.id)}
                            className={cn("w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all",
                              captionStyle === style.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/40")}>
                            <div className="w-24 shrink-0 bg-muted rounded-lg h-10 flex items-center justify-center overflow-hidden border border-border">
                              <span className={cn("text-center px-1 truncate", style.className)}>{style.preview}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">{style.label}</p>
                              <p className="text-xs text-muted-foreground">{style.description}</p>
                            </div>
                            {captionStyle === style.id && <Check className="w-4 h-4 text-primary shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Transcript */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Type className="w-4 h-4" />Transcript
                        {transcribing && <Badge variant="secondary" className="gap-1 text-xs animate-pulse"><Loader2 className="w-3 h-3 animate-spin" />Transcribing…</Badge>}
                        {!transcribing && transcript && <Badge variant="secondary" className="gap-1 text-xs text-primary"><Check className="w-3 h-3" />Auto-transcribed</Badge>}
                      </label>
                      {transcribeError && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" />{transcribeError}</p>}
                      <p className="text-xs text-muted-foreground">{transcribing ? "AI is transcribing — this may take a minute…" : "Auto-transcribed. Edit below or paste your own."}</p>
                      <Textarea value={transcript} onChange={e => setTranscript(e.target.value)} placeholder={transcribing ? "Transcribing..." : "Paste your transcript here..."} className="min-h-[100px] text-sm" disabled={transcribing} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button size="lg" onClick={handleGenerate} disabled={transcribing} className="gap-2 px-8">
                    {transcribing ? <><Loader2 className="w-4 h-4 animate-spin" />Waiting for transcript…</> : <><Sparkles className="w-4 h-4" />Generate {clipCount} Clips<ChevronRight className="w-4 h-4" /></>}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Generating & Rendering */}
            {step === 3 && (
              <div className="space-y-8 animate-fade-in">
                <div className="flex flex-col items-center space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold text-foreground">{clips.length > 0 ? "Rendering Your Clips" : "AI is Analyzing Your Video"}</h2>
                    <p className="text-muted-foreground text-sm">{clips.length > 0 ? `${renderedCount}/${clips.length} clips ready` : "This usually takes 15–30 seconds"}</p>
                  </div>
                  <div className="w-full max-w-md space-y-3">
                    {clips.length > 0 ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-2">{renderingCount > 0 && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}{renderingCount > 0 ? "Rendering clips…" : "All clips rendered!"}</span>
                          <span className="font-semibold text-foreground">{renderedCount}/{clips.length}</span>
                        </div>
                        <Progress value={clips.length > 0 ? (renderedCount / clips.length) * 100 : 0} className="h-3" />
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">{genStatus}</span><span className="font-medium text-foreground">{Math.round(genProgress)}%</span></div>
                        <Progress value={genProgress} className="h-3" />
                      </>
                    )}
                  </div>
                </div>

                {clips.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {clips.map(clip => <ClipCard key={clip.clip_index} clip={clip} captionStyle={captionStyle} renderState={renderStates[clip.clip_index]} onRender={() => handleRenderSingle(clip)} expiresAt={currentExpiresAt || undefined} />)}
                  </div>
                )}

                {clips.length === 0 && (
                  <div className="flex flex-col items-center space-y-6">
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                      <img src={clipaiLogo} alt="Clip AI" className="w-24 h-24 rounded-2xl object-contain relative z-10 animate-pulse" />
                    </div>
                    <div className="w-full max-w-md space-y-2">
                      {["Detecting high-retention moments", "Identifying hook-style statements", "Scoring engagement potential", "Optimizing clip boundaries", "Ranking by virality score"].map((item, i) => {
                        const done = genProgress > (i + 1) * 18;
                        return (
                          <div key={item} className={cn("flex items-center gap-2 text-sm transition-all", done ? "text-foreground" : "text-muted-foreground")}>
                            {done ? <Check className="w-4 h-4 text-primary shrink-0" /> : <Loader2 className={cn("w-4 h-4 shrink-0", genProgress > i * 18 ? "animate-spin text-primary" : "")} />}
                            {item}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 4: Results / Download */}
            {step === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground flex items-center gap-2"><Trophy className="w-6 h-6 text-primary" />{clips.length} Clips Generated!</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {renderingCount > 0 ? <>Rendering… {renderedCount}/{clips.length} ready.</> : renderedCount === clips.length && clips.length > 0 ? <>All clips rendered! Preview and download below.</> : <>Ranked from most to least viral.</>}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => { setClips([]); setStep(2); }}><RefreshCw className="w-3 h-3" />Regenerate</Button>
                    <Button size="sm" className="gap-1" onClick={handleRenderAll} disabled={renderingAll || renderingCount > 0}>
                      {renderingAll || renderingCount > 0 ? <><Loader2 className="w-4 h-4 animate-spin" />Rendering {renderedCount}/{clips.length}…</> : <><Film className="w-4 h-4" />Re-render All</>}
                    </Button>
                    {/* Download All ZIP */}
                    {renderedCount > 0 && (
                      <Button size="sm" variant="secondary" className="gap-1" onClick={handleDownloadAll} disabled={downloadingZip}>
                        {downloadingZip ? <><Loader2 className="w-3 h-3 animate-spin" />Zipping…</> : <><PackageOpen className="w-3 h-3" />Download All ({renderedCount})</>}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Render progress bar */}
                {(renderingCount > 0 || renderedCount > 0) && (
                  <div className="bg-card border border-border rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">{renderingCount > 0 && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}{renderingCount > 0 ? "Rendering clips…" : "All clips rendered!"}</span>
                      <span className="font-semibold text-foreground">{renderedCount}/{clips.length}</span>
                    </div>
                    <Progress value={clips.length > 0 ? (renderedCount / clips.length) * 100 : 0} className="h-2" />
                    {Object.values(renderStates).some(r => r.status === "failed") && <p className="text-xs text-destructive">{Object.values(renderStates).filter(r => r.status === "failed").length} clip(s) failed</p>}
                  </div>
                )}

                {analysisSummary && (
                  <div className="bg-card border border-border rounded-xl p-4 flex gap-3">
                    <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div><p className="text-sm font-semibold text-foreground mb-1">AI Analysis</p><p className="text-sm text-muted-foreground">{analysisSummary}</p></div>
                  </div>
                )}

                {/* Clip to Sell CTA */}
                <div className={cn("rounded-xl border p-4 space-y-3 transition-all", clipToSellEnabled ? "border-primary bg-primary/5" : "border-border bg-card")}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ShoppingBag className={cn("w-5 h-5", clipToSellEnabled ? "text-primary" : "text-muted-foreground")} />
                      <div><p className="text-sm font-semibold text-foreground">Clip to Sell</p><p className="text-xs text-muted-foreground">Add a CTA overlay to the final 3 seconds</p></div>
                    </div>
                    <Switch checked={clipToSellEnabled} onCheckedChange={setClipToSellEnabled} />
                  </div>
                  {clipToSellEnabled && (
                    <div className="space-y-2 pl-8">
                      <label className="text-xs font-medium text-foreground block">CTA Text</label>
                      <Input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="e.g. Link in bio to get my eBook" className="text-sm" />
                      <p className="text-xs text-muted-foreground">Burned into the clip in the same style as your captions.</p>
                    </div>
                  )}
                </div>

                {/* Stats bar */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Top Score", value: `${clips[0]?.engagement_score ?? 0}/100`, icon: Star },
                    { label: "Total Duration", value: `${clips.reduce((s, c) => s + c.duration_seconds, 0)}s`, icon: Clock },
                    { label: "Caption Style", value: CAPTION_STYLES.find(s => s.id === captionStyle)?.label ?? captionStyle, icon: Type },
                  ].map(stat => (
                    <div key={stat.label} className="bg-card border border-border rounded-xl p-3 text-center">
                      <stat.icon className="w-4 h-4 text-primary mx-auto mb-1" /><p className="text-sm font-bold text-foreground">{stat.value}</p><p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Clips grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {clips.map(clip => <ClipCard key={clip.clip_index} clip={clip} captionStyle={captionStyle} renderState={renderStates[clip.clip_index]} onRender={() => handleRenderSingle(clip)} expiresAt={currentExpiresAt || undefined} />)}
                </div>

                {/* Platform tips */}
                <PlatformTips platform={platform} clips={clips} />

                {/* Back */}
                <div className="text-center pt-4">
                  <Button variant="ghost" onClick={() => { startNewProject(); setViewMode("projects"); fetchProjects(); }}>Back to Projects</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
